import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  Mail,
  ShieldCheck,
  Clock,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";

const INITIAL_FORM = {
  name: "",
  email: "",
  role: "",
  subject: "",
  message: "",
};

export default function Kontakt() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (key) => (event) => {
    setForm((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      await axios.post("/api/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });

      setDone(true);
      setForm(INITIAL_FORM);

      toast.success("Nachricht gesendet.", {
        description: "Wir melden uns in Kürze bei dir.",
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        "Etwas ist schiefgelaufen. Versuche es erneut.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      testId="kontakt-page"
      eyebrow="Kontakt"
      title={
        <>
          Schreib uns.{" "}
          <span className="text-white/40">
            Sachlich. Vertraulich.
          </span>
        </>
      }
      subtitle="Fragen zu BeParent, zur Datenschutzarchitektur oder zur Verwendung im Verfahren? Wir antworten in der Regel innerhalb von zwei Werktagen — persönlich, ohne Ticketing-System."
    >
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <aside className="space-y-4 lg:col-span-4">
          <InfoCard
            icon={<Mail size={16} className="text-[#00FAF8]" />}
            label="E-Mail"
            value="beparent.app@web.de"
            href="mailto:beparent.app@web.de"
          />

          <InfoCard
            icon={<Clock size={16} className="text-[#00FAF8]" />}
            label="Antwortzeit"
            value="≤ 2 Werktage"
          />

          <InfoCard
            icon={
              <ShieldCheck
                size={16}
                className="text-[#00FAF8]"
              />
            }
            label="Diskretion"
            value="Vertraulich · kein Tracking"
          />

          <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
            <div className="mb-2 font-mono-bp text-[10px] uppercase tracking-[0.3em] text-white/40">
              Hinweis
            </div>

            <p className="text-sm leading-relaxed text-white/60">
              Bitte übermittle keine sensiblen Verfahrensdaten per
              E-Mail. Für Fallakten nutze die App — Daten bleiben dort
              verschlüsselt und lokal auf deinem Gerät.
            </p>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <div className="relative overflow-hidden rounded-[32px] border border-white/8 bg-gradient-to-br from-[#152036] via-[#0F172A] to-[#0B1120] p-8 md:p-10">
            <div
              aria-hidden
              className="absolute -right-40 -top-40 h-[440px] w-[440px] rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,250,248,0.14), transparent 60%)",
                filter: "blur(30px)",
              }}
            />

            <div
              aria-hidden
              className="absolute inset-0 bp-grid opacity-30"
            />

            <div className="relative">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-success"
                  className="py-8"
                >
                  <div className="inline-flex items-center gap-3 rounded-full border border-[#00FAF8]/40 bg-[#00FAF8]/10 px-5 py-3">
                    <Check
                      size={16}
                      className="text-[#00FAF8]"
                    />

                    <span className="text-white/90">
                      Nachricht gesendet — danke.
                    </span>
                  </div>

                  <p className="mt-6 max-w-lg text-white/60">
                    Wir melden uns in der Regel innerhalb von zwei
                    Werktagen bei dir. In dringenden Fällen erreichst
                    du uns direkt unter{" "}
                    <a
                      href="mailto:beparent.app@web.de"
                      className="bp-link text-[#00FAF8]"
                    >
                      beparent.app@web.de
                    </a>
                    .
                  </p>

                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-6 text-sm text-white/50 transition-colors hover:text-[#00FAF8]"
                  >
                    Weitere Nachricht senden
                  </button>
                </motion.div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="space-y-5"
                  noValidate
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Name" required>
                      <input
                        type="text"
                        required
                        autoComplete="name"
                        maxLength={100}
                        value={form.name}
                        onChange={setField("name")}
                        data-testid="contact-name"
                        placeholder="Vor- und Nachname"
                        className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#00FAF8]/60 focus:bg-white/[0.06]"
                      />
                    </Field>

                    <Field label="E-Mail" required>
                      <input
                        type="email"
                        required
                        autoComplete="email"
                        maxLength={254}
                        value={form.email}
                        onChange={setField("email")}
                        data-testid="contact-email"
                        placeholder="deine@e-mail.de"
                        className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#00FAF8]/60 focus:bg-white/[0.06]"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Rolle (optional)">
                      <select
                        value={form.role}
                        onChange={setField("role")}
                        data-testid="contact-role"
                        className="w-full appearance-none rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white outline-none transition-colors focus:border-[#00FAF8]/60"
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg fill='%2394A3B8' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'><path d='M5.5 8l4.5 4.5L14.5 8z'/></svg>\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 1rem center",
                        }}
                      >
                        <option value="" className="bg-[#0F172A]">
                          Rolle wählen
                        </option>
                        <option
                          value="Elternteil"
                          className="bg-[#0F172A]"
                        >
                          Elternteil
                        </option>
                        <option
                          value="Anwalt"
                          className="bg-[#0F172A]"
                        >
                          Anwalt
                        </option>
                        <option
                          value="Jugendamt"
                          className="bg-[#0F172A]"
                        >
                          Jugendamt
                        </option>
                        <option
                          value="Verfahrensbeistand"
                          className="bg-[#0F172A]"
                        >
                          Verfahrensbeistand
                        </option>
                        <option
                          value="Gutachter"
                          className="bg-[#0F172A]"
                        >
                          Gutachter:in
                        </option>
                        <option
                          value="Psychologe"
                          className="bg-[#0F172A]"
                        >
                          Psychologe:in
                        </option>
                        <option
                          value="Sozialarbeiter"
                          className="bg-[#0F172A]"
                        >
                          Sozialarbeiter:in
                        </option>
                        <option
                          value="Mediator"
                          className="bg-[#0F172A]"
                        >
                          Mediator:in
                        </option>
                        <option
                          value="Gericht"
                          className="bg-[#0F172A]"
                        >
                          Gericht
                        </option>
                        <option
                          value="Presse"
                          className="bg-[#0F172A]"
                        >
                          Presse
                        </option>
                        <option
                          value="Sonstiges"
                          className="bg-[#0F172A]"
                        >
                          Sonstiges
                        </option>
                      </select>
                    </Field>

                    <Field label="Betreff (optional)">
                      <input
                        type="text"
                        maxLength={160}
                        value={form.subject}
                        onChange={setField("subject")}
                        data-testid="contact-subject"
                        placeholder="z. B. Datenschutz, Kooperation, Presse"
                        className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#00FAF8]/60 focus:bg-white/[0.06]"
                      />
                    </Field>
                  </div>

                  <Field label="Nachricht" required>
                    <textarea
                      required
                      maxLength={5000}
                      value={form.message}
                      onChange={setField("message")}
                      data-testid="contact-message"
                      rows={6}
                      placeholder="Wie können wir helfen?"
                      className="min-h-[140px] w-full resize-y rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white outline-none transition-colors placeholder:text-white/30 focus:border-[#00FAF8]/60 focus:bg-white/[0.06]"
                    />
                  </Field>

                  <div className="flex flex-col-reverse items-start justify-between gap-4 pt-2 md:flex-row md:items-center">
                    <p className="max-w-md font-mono-bp text-xs text-white/40">
                      Mit dem Absenden stimmst du der Verarbeitung
                      deiner Angaben zur Beantwortung deiner Anfrage
                      zu.
                    </p>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit"
                      className="group inline-flex items-center gap-2 rounded-full bg-[#00FAF8] px-7 py-3.5 text-[15px] font-medium text-[#0F172A] transition-shadow hover:shadow-[0_0_40px_rgba(0,250,248,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading
                        ? "Wird gesendet…"
                        : "Nachricht senden"}

                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function Field({ label, required = false, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-2 font-mono-bp text-[11px] uppercase tracking-[0.25em] text-white/50">
        {label}

        {required && (
          <span className="text-[#00FAF8]">·</span>
        )}
      </span>

      {children}
    </label>
  );
}

function InfoCard({ icon, label, value, href }) {
  const Wrapper = href ? "a" : "div";

  return (
    <Wrapper
      {...(href ? { href } : {})}
      className="block rounded-2xl border border-white/5 bg-white/[0.02] p-5 transition-colors hover:border-[#00FAF8]/40"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#00FAF8]/25 bg-[#00FAF8]/10">
          {icon}
        </span>

        <div>
          <div className="font-mono-bp text-[10px] uppercase tracking-[0.3em] text-white/40">
            {label}
          </div>

          <div className="mt-0.5 text-[15px] text-white">
            {value}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}